import { Dialog, Transition } from "@headlessui/react";
import { CancelSmallMinor } from "@shopify/polaris-icons";
import type { ReactNode } from "react";
import { Fragment, useEffect, useState } from "react";
import ReactDOM from "react-dom";

export type { ModalSectionProps } from "./ModalSection";
export { ModalSection } from "./ModalSection";

export interface ModalProps {
  id: string;
  testId?: string;
  active: boolean;
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}

const MODAL_ROOT = "modal-root";

export function Modal(props: ModalProps) {
  const { id, testId, active, header, footer, children, onClose } = props;

  const [visible, setVisible] = useState(active);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    const root = document.getElementById(MODAL_ROOT);

    if (root) {
      const instance = root.querySelector(`#${id}`);

      if (!instance) {
        const div = document.createElement("div");
        div.id = id;
        root.appendChild(div);
      }
    }
  }, [id]);

  useEffect(() => {
    if (active && !visible) {
      setVisible(true);
    } else if (!active && visible) {
      setTimeout(() => setVisible(false), 300);
    }
  }, [active, visible]);

  if (!mounted) {
    return null;
  }

  const instance = document.querySelector(`#${MODAL_ROOT} #${id}`);

  if (!instance) {
    return null;
  }

  const markup = (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onClose}
        data-test-id={testId}
        style={{ top: "initial" }}>
        <div
          className="flex text-center md:block md:px-2 lg:px-4"
          style={{ fontSize: 0 }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-50"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-50"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-75 transition-opacity md:block" />
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden md:inline-block md:align-middle md:h-screen"
            aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-50"
            enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            enterTo="opacity-100 translate-y-0 md:scale-100"
            leave="ease-in duration-50"
            leaveFrom="opacity-100 translate-y-0 md:scale-100"
            leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95">
            <div className="flex text-base text-left transform transition w-full md:inline-block md:max-w-2xl md:px-4 md:my-8 md:align-middle lg:max-w-3xl">
              <div className="modal--content">
                <div className="modal--header">
                  <div className="modal--header--content">{header}</div>
                  <button
                    type="button"
                    className="modal--header--close"
                    onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <CancelSmallMinor
                      className="h-6 w-6"
                      aria-hidden="true"
                      fill="currentColor"
                    />
                  </button>
                </div>
                <div className="modal--body">{children}</div>
                {footer ? <div className="modal--footer">{footer}</div> : null}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );

  return ReactDOM.createPortal(markup, instance);
}
